import { 
  collection, 
  getDocs, 
  doc, 
  setDoc, 
  query, 
  orderBy, 
  limit,
  where,
  Timestamp 
} from 'firebase/firestore';
import { db } from '../services/firebase';

/**
 * Data Migration Utility for Event Logs and Reports
 * This script analyzes existing Firebase data and creates historical event logs
 */

class EventLogMigrator {
  constructor() {
    this.migratedEvents = [];
    this.migrationStats = {
      totalEvents: 0,
      adminEvents: 0,
      userEvents: 0,
      chatEvents: 0,
      leadEvents: 0,
      jobEvents: 0,
      contentEvents: 0,
      errors: 0
    };
  }

  /**
   * Generate a historical event log entry
   */
  createEventLog(type, action, details, timestamp, userId = null, userEmail = null) {
    const eventId = `migrated_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    return {
      id: eventId,
      type,
      action,
      details,
      timestamp: timestamp || Timestamp.now(),
      userId: userId || 'system',
      userEmail: userEmail || 'system@scalovate.com',
      userRole: userId ? 'User' : 'System',
      metadata: {
        migrated: true,
        migratedAt: Timestamp.now(),
        source: 'data_migration'
      },
      ipAddress: '127.0.0.1',
      userAgent: 'Data Migration Script'
    };
  }

  /**
   * Migrate Admin Users data to event logs
   */
  async migrateAdminUsers() {
    console.log('🔄 Migrating admin users data...');
    
    try {
      const adminUsersRef = collection(db, 'system', 'admins', 'users');
      const snapshot = await getDocs(adminUsersRef);
      
      if (snapshot.empty) {
        // Try alternative path
        const altRef = collection(db, 'adminUsers');
        const altSnapshot = await getDocs(altRef);
        
        if (!altSnapshot.empty) {
          altSnapshot.forEach(doc => {
            const admin = doc.data();
            const createdAt = admin.createdAt || admin.dateCreated || Timestamp.now();
            
            // Create admin creation event
            const event = this.createEventLog(
              'admin_management',
              'admin_created',
              `Admin user created: ${admin.email} (${admin.role || 'Admin'})`,
              createdAt,
              admin.uid || doc.id,
              admin.email
            );
            
            this.migratedEvents.push(event);
            this.migrationStats.adminEvents++;
            
            // Create login events if lastLogin exists
            if (admin.lastLogin) {
              const loginEvent = this.createEventLog(
                'admin_auth',
                'admin_login',
                `Admin login: ${admin.email}`,
                admin.lastLogin,
                admin.uid || doc.id,
                admin.email
              );
              
              this.migratedEvents.push(loginEvent);
              this.migrationStats.adminEvents++;
            }
          });
        }
      } else {
        snapshot.forEach(doc => {
          const admin = doc.data();
          const createdAt = admin.createdAt || Timestamp.now();
          
          const event = this.createEventLog(
            'admin_management',
            'admin_created',
            `Admin user created: ${admin.email} (${admin.role || 'Admin'})`,
            createdAt,
            admin.uid || doc.id,
            admin.email
          );
          
          this.migratedEvents.push(event);
          this.migrationStats.adminEvents++;
        });
      }
      
      console.log(`✅ Migrated ${this.migrationStats.adminEvents} admin events`);
    } catch (error) {
      console.error('❌ Error migrating admin users:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Migrate Users data to event logs
   */
  async migrateUsers() {
    console.log('🔄 Migrating users data...');
    
    try {
      const usersRef = collection(db, 'users');
      const snapshot = await getDocs(usersRef);
      
      snapshot.forEach(doc => {
        const user = doc.data();
        const createdAt = user.createdAt || user.dateJoined || Timestamp.now();
        
        // Create user registration event
        const event = this.createEventLog(
          'user_management',
          'user_registered',
          `User registered: ${user.email || user.displayName || 'Unknown'}`,
          createdAt,
          doc.id,
          user.email
        );
        
        this.migratedEvents.push(event);
        this.migrationStats.userEvents++;
        
        // Create login events if lastLogin exists
        if (user.lastLogin) {
          const loginEvent = this.createEventLog(
            'user_auth',
            'user_login',
            `User login: ${user.email || user.displayName}`,
            user.lastLogin,
            doc.id,
            user.email
          );
          
          this.migratedEvents.push(loginEvent);
          this.migrationStats.userEvents++;
        }
      });
      
      console.log(`✅ Migrated ${this.migrationStats.userEvents} user events`);
    } catch (error) {
      console.error('❌ Error migrating users:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Migrate Chats data to event logs
   */
  async migrateChats() {
    console.log('🔄 Migrating chats data...');
    
    try {
      const chatsRef = collection(db, 'chats');
      const snapshot = await getDocs(chatsRef);
      
      snapshot.forEach(doc => {
        const chat = doc.data();
        const createdAt = chat.createdAt || chat.timestamp || Timestamp.now();
        
        // Create chat started event
        const event = this.createEventLog(
          'chat',
          'chat_started',
          `Chat session started by ${chat.userEmail || 'Anonymous'}`,
          createdAt,
          chat.userId,
          chat.userEmail
        );
        
        this.migratedEvents.push(event);
        this.migrationStats.chatEvents++;
        
        // Create message events for each message
        if (chat.messages && Array.isArray(chat.messages)) {
          chat.messages.forEach((message, index) => {
            const messageEvent = this.createEventLog(
              'chat',
              'message_sent',
              `Message sent: ${message.role} - ${message.content?.substring(0, 100)}...`,
              message.timestamp || createdAt,
              chat.userId,
              chat.userEmail
            );
            
            this.migratedEvents.push(messageEvent);
            this.migrationStats.chatEvents++;
          });
        }
      });
      
      console.log(`✅ Migrated ${this.migrationStats.chatEvents} chat events`);
    } catch (error) {
      console.error('❌ Error migrating chats:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Migrate Leads data to event logs
   */
  async migrateLeads() {
    console.log('🔄 Migrating leads data...');
    
    try {
      const leadsRef = collection(db, 'leads');
      const snapshot = await getDocs(leadsRef);
      
      snapshot.forEach(doc => {
        const lead = doc.data();
        const createdAt = lead.createdAt || lead.timestamp || Timestamp.now();
        
        // Create lead generated event
        const event = this.createEventLog(
          'lead_management',
          'lead_generated',
          `Lead generated: ${lead.email || lead.name || 'Unknown'} - ${lead.interest || 'General inquiry'}`,
          createdAt,
          lead.userId,
          lead.email
        );
        
        this.migratedEvents.push(event);
        this.migrationStats.leadEvents++;
        
        // Create status change events if status history exists
        if (lead.statusHistory && Array.isArray(lead.statusHistory)) {
          lead.statusHistory.forEach(status => {
            const statusEvent = this.createEventLog(
              'lead_management',
              'lead_status_changed',
              `Lead status changed to: ${status.status}`,
              status.timestamp || createdAt,
              status.changedBy,
              lead.email
            );
            
            this.migratedEvents.push(statusEvent);
            this.migrationStats.leadEvents++;
          });
        }
      });
      
      console.log(`✅ Migrated ${this.migrationStats.leadEvents} lead events`);
    } catch (error) {
      console.error('❌ Error migrating leads:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Migrate Job Applications data to event logs
   */
  async migrateJobApplications() {
    console.log('🔄 Migrating job applications data...');
    
    try {
      const jobsRef = collection(db, 'jobApplications');
      const snapshot = await getDocs(jobsRef);
      
      snapshot.forEach(doc => {
        const job = doc.data();
        const createdAt = job.submittedAt || job.createdAt || Timestamp.now();
        
        // Create job application event
        const event = this.createEventLog(
          'job_management',
          'job_application_submitted',
          `Job application submitted: ${job.email || job.name || 'Unknown'} for ${job.position || 'Unknown position'}`,
          createdAt,
          null,
          job.email
        );
        
        this.migratedEvents.push(event);
        this.migrationStats.jobEvents++;
      });
      
      console.log(`✅ Migrated ${this.migrationStats.jobEvents} job application events`);
    } catch (error) {
      console.error('❌ Error migrating job applications:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Migrate Content data to event logs
   */
  async migrateContent() {
    console.log('🔄 Migrating content data...');
    
    try {
      const contentRef = collection(db, 'content');
      const snapshot = await getDocs(contentRef);
      
      snapshot.forEach(doc => {
        const content = doc.data();
        const createdAt = content.createdAt || content.dateAdded || Timestamp.now();
        
        // Create content added event
        const event = this.createEventLog(
          'content_management',
          'content_added',
          `Content added: ${content.title || content.name || 'Unknown'} (${content.type || 'Unknown type'})`,
          createdAt,
          content.addedBy,
          content.addedByEmail || 'system@scalovate.com'
        );
        
        this.migratedEvents.push(event);
        this.migrationStats.contentEvents++;
        
        // Create update events if lastModified exists
        if (content.lastModified && content.lastModified !== createdAt) {
          const updateEvent = this.createEventLog(
            'content_management',
            'content_updated',
            `Content updated: ${content.title || content.name || 'Unknown'}`,
            content.lastModified,
            content.lastModifiedBy,
            content.lastModifiedByEmail || 'system@scalovate.com'
          );
          
          this.migratedEvents.push(updateEvent);
          this.migrationStats.contentEvents++;
        }
      });
      
      console.log(`✅ Migrated ${this.migrationStats.contentEvents} content events`);
    } catch (error) {
      console.error('❌ Error migrating content:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Save all migrated events to Firebase
   */
  async saveEventLogs() {
    console.log('💾 Saving event logs to Firebase...');
    
    try {
      const batchSize = 50; // Firestore batch limit
      const batches = [];
      
      for (let i = 0; i < this.migratedEvents.length; i += batchSize) {
        const batch = this.migratedEvents.slice(i, i + batchSize);
        batches.push(batch);
      }
      
      for (const batch of batches) {
        const promises = batch.map(event => {
          const eventRef = doc(db, 'system', 'eventLogs', event.id);
          return setDoc(eventRef, event);
        });
        
        await Promise.all(promises);
        console.log(`✅ Saved batch of ${batch.length} events`);
      }
      
      this.migrationStats.totalEvents = this.migratedEvents.length;
      console.log(`🎉 Successfully saved ${this.migrationStats.totalEvents} event logs!`);
      
    } catch (error) {
      console.error('❌ Error saving event logs:', error);
      this.migrationStats.errors++;
    }
  }

  /**
   * Generate migration summary report
   */
  generateMigrationReport() {
    const report = {
      migrationDate: new Date().toISOString(),
      totalEvents: this.migrationStats.totalEvents,
      eventBreakdown: {
        adminEvents: this.migrationStats.adminEvents,
        userEvents: this.migrationStats.userEvents,
        chatEvents: this.migrationStats.chatEvents,
        leadEvents: this.migrationStats.leadEvents,
        jobEvents: this.migrationStats.jobEvents,
        contentEvents: this.migrationStats.contentEvents
      },
      errors: this.migrationStats.errors,
      success: this.migrationStats.errors === 0
    };
    
    console.log('\n📊 MIGRATION REPORT:');
    console.log('====================');
    console.log(`Total Events Migrated: ${report.totalEvents}`);
    console.log(`Admin Events: ${report.eventBreakdown.adminEvents}`);
    console.log(`User Events: ${report.eventBreakdown.userEvents}`);
    console.log(`Chat Events: ${report.eventBreakdown.chatEvents}`);
    console.log(`Lead Events: ${report.eventBreakdown.leadEvents}`);
    console.log(`Job Application Events: ${report.eventBreakdown.jobEvents}`);
    console.log(`Content Events: ${report.eventBreakdown.contentEvents}`);
    console.log(`Errors: ${report.errors}`);
    console.log(`Status: ${report.success ? '✅ SUCCESS' : '❌ PARTIAL SUCCESS'}`);
    console.log('====================\n');
    
    return report;
  }

  /**
   * Run complete migration process
   */
  async runMigration() {
    console.log('🚀 Starting Event Logs Migration...\n');
    
    try {
      // Migrate all data types
      await this.migrateAdminUsers();
      await this.migrateUsers();
      await this.migrateChats();
      await this.migrateLeads();
      await this.migrateJobApplications();
      await this.migrateContent();
      
      // Save all events to Firebase
      await this.saveEventLogs();
      
      // Generate and return report
      return this.generateMigrationReport();
      
    } catch (error) {
      console.error('❌ Migration failed:', error);
      this.migrationStats.errors++;
      return this.generateMigrationReport();
    }
  }
}

/**
 * Initialize and run migration
 */
export const migrateExistingData = async () => {
  const migrator = new EventLogMigrator();
  return await migrator.runMigration();
};

/**
 * Quick migration function for admin panel
 */
export const runDataMigration = async () => {
  console.log('Starting data migration...');
  
  try {
    const result = await migrateExistingData();
    
    if (result.success) {
      alert(`Migration completed successfully!\n\nTotal Events: ${result.totalEvents}\nAdmin Events: ${result.eventBreakdown.adminEvents}\nUser Events: ${result.eventBreakdown.userEvents}\nChat Events: ${result.eventBreakdown.chatEvents}\nLead Events: ${result.eventBreakdown.leadEvents}\nJob Events: ${result.eventBreakdown.jobEvents}\nContent Events: ${result.eventBreakdown.contentEvents}`);
    } else {
      alert(`Migration completed with ${result.errors} errors.\n\nCheck console for details.\n\nTotal Events: ${result.totalEvents}`);
    }
    
    return result;
  } catch (error) {
    console.error('Migration error:', error);
    alert('Migration failed. Check console for details.');
    return null;
  }
};

export default EventLogMigrator; 